import React, { useEffect, useState } from 'react';
import {
  View,
  StyleSheet,
  ScrollView,
  Pressable,
} from 'react-native';
import CCText from '@/Atoms/CCText';
import Box from '@/Atoms/Box';
import CCColors from '@/Utils/CCColors';
import { FontSizes } from '@/Utils/Dimensions';
import { LinearGradient } from 'expo-linear-gradient';
import Spacer from '@/Atoms/Spacer';

import CCButton from '@/Atoms/CCButton';
import Feather from '@expo/vector-icons/Feather';
import Ionicons from '@expo/vector-icons/Ionicons';
import { useMediaQuery } from '@/Hooks/useMediaQuery/useMediaQuery';
import Tooltip from '@/Atoms/Tooltip';

type OpeningReportSectionNoteKey =
  | 'overall_opening_score'
  | 'performance_summary'
  | 'game_by_game'
  | 'principle_wise'
  | 'opening_principles_bars'
  | 'recurring_errors'
  | 'model_games';

export type OirColorTabSlice = {
  has_games?: boolean;
  games_analyzed?: number;
  opening_score?: number;
  opening_accuracy?: number;
  best_game_score?: number;
  lowest_game_score?: number;
  game_by_game_performance?: { game_uuid?: string; score?: number; game_label?: string }[];
  improvements?: { title: string; score: number; color: string }[];
  principle_wise_scores?: {
    principle?: string;
    score?: number;
    followed?: number;
    violated?: number;
    avg_rating?: number;
    confidence?: string;
    samples?: number;
    status_label?: string;
    summary_line?: string;
    confidence_note?: string;
  }[];
  recurring_errors?: { title?: string; count?: number; count_label?: string }[];
  recurring_opponent_errors?: { title?: string; count?: number; count_label?: string }[];
  opponent_errors?: { title?: string; count?: number; count_label?: string }[];
  most_broken_principle?: { title?: string; quote?: string };
  score_band?: { key?: string; label?: string; summary?: string };
  performance_cards?: { title: string; content: string; tone?: string }[];
};

function performanceCardSurface(card: {
  tone?: string;
  bgColor?: string;
  borderColor?: string;
}): { backgroundColor: string; borderColor: string } {
  if (card.bgColor && card.borderColor) {
    return { backgroundColor: card.bgColor, borderColor: card.borderColor };
  }
  if (card.tone === 'green') {
    return { backgroundColor: 'rgba(61, 171, 158, 0.16)', borderColor: CCColors.Green };
  }
  if (card.tone === 'pink') {
    return { backgroundColor: 'rgba(219, 122, 204, 0.16)', borderColor: CCColors.Pink };
  }
  return {
    backgroundColor: 'rgba(61, 171, 158, 0.12)',
    borderColor: 'rgba(38, 35, 34, 0.12)',
  };
}

function principleStatusColor(label?: string): string {
  switch (label) {
    case 'Strong':
      return CCColors.Green;
    case 'Good':
      return '#2A9D8F';
    case 'Mixed':
      return '#C98A3D';
    case 'Needs work':
      return CCColors.Red;
    default:
      return 'rgba(77, 63, 55, 0.55)';
  }
}

function overallScoreBandColor(label?: string): string {
  switch (label) {
    case 'Excellent':
    case 'Strong':
      return CCColors.Green;
    case 'Solid':
      return '#2A9D8F';
    case 'Developing':
      return '#C98A3D';
    case 'Needs focus':
      return CCColors.Red;
    default:
      return 'rgba(77, 63, 55, 0.55)';
  }
}

interface OpeningPerformanceReportProps {
  onBackPress?: () => void;
  userName?: string;
  userPoints?: number;
  userLevel?: string;
  streakDays?: number;
  pointsToNextLevel?: number;
  openingScore?: number;
  openingAccuracy?: number;
  bestGameScore?: number;
  lowestGameScore?: number;
  reportDate?: string;
  gamesAnalyzed?: number;
  gameScores?: number[];
  gameScoreCards?: {game_uuid?: string; score?: number; game_label?: string}[];
  onOpenGameReviewFromScore?: (gameUuid: string) => void;
  onRunAssessment?: () => void;
  onSeeLeaderboard?: () => void;
  onOpenModelGame?: (index: number) => void;
  onSolvePatternDrills?: () => void;
  onTrainPunishments?: () => void;
  onSeeAllGoodMoves?: () => void;
  onPracticeStrength?: () => void;
  onSeeViolations?: () => void;
  /** Top-right header control (e.g. Download PDF). Replaces the old opening-name dropdown. */
  headerRightAction?: React.ReactNode;
  /** @deprecated Opening label was shown in a header dropdown; report is single-opening from API. */
  selectedOpening?: string;
  onOpeningChange?: (opening: string) => void;
  performanceCardsData?: {
    title: string;
    content: string;
    tone?: string;
    color?: string;
    bgColor?: string;
    borderColor?: string;
  }[];
  /** Short intro under the report title (from API `report_intro`). */
  reportIntro?: string;
  /** Score band label + summary (from API `score_band`). */
  scoreBand?: { key?: string; label?: string; summary?: string };
  /** Per-section helper copy (from API `section_notes`). */
  sectionNotes?: Partial<Record<OpeningReportSectionNoteKey, string>>;
  improvementsData?: { title: string; score: number; color: string }[];
  recurringErrorsData?: { title: string; count: string }[];
  opponentErrorsData?: { title: string; count: string }[];
  criticalPositionsData?: { title: string; count: string }[];
  modelGamesData?: {
    title: string;
    reason: string;
    model_game_id?: string;
    pgn?: string;
    event?: string;
  }[];
  mostBrokenPrincipleQuote?: string;
  /** Aggregated principle scores from OIR API (`frontend_view.principle_wise_scores`). */
  principleWiseScores?: {
    principle?: string;
    score?: number;
    followed?: number;
    violated?: number;
    avg_rating?: number;
    confidence?: string;
    samples?: number;
    status_label?: string;
    summary_line?: string;
    confidence_note?: string;
  }[];
  onPracticePrinciples?: (principleTitle: string) => void;
  /** When set, All / White / Black tabs filter scores and breakdowns to that slice (`frontend_view.color_breakdown`). */
  colorBreakdown?: {
    all: OirColorTabSlice;
    white: OirColorTabSlice;
    black: OirColorTabSlice;
  };
  /** Band definitions from API (`frontend_view.opening_score_band_legend`). */
  scoreBandLegend?: { key?: string; label?: string; range?: string; description?: string }[];
  /** Headless PDF: hide chrome and actions; expose ready marker for Playwright / print. */
  forPdfExport?: boolean;
}


const OpeningPerformanceReport: React.FC<OpeningPerformanceReportProps> = ({
  onBackPress,
  userName = 'Sumit Mohan',
  userPoints = 1250,
  userLevel = 'Beginner',
  streakDays = 2,
  pointsToNextLevel = 10,
  openingScore = 75,
  openingAccuracy,
  bestGameScore = 89,
  lowestGameScore = 61,
  reportDate = 'May 18, 2025',
  gamesAnalyzed = 6,
  gameScores = [89, 89, 89, 89, 89, 89],
  gameScoreCards,
  onOpenGameReviewFromScore,
  onRunAssessment,
  onSeeLeaderboard,
  onOpenModelGame,
  onSolvePatternDrills,
  onTrainPunishments,
  onSeeAllGoodMoves,
  onPracticeStrength,
  onSeeViolations,
  headerRightAction,
  selectedOpening: _selectedOpening,
  onOpeningChange: _onOpeningChange,
  performanceCardsData,
  reportIntro,
  scoreBand,
  sectionNotes,
  improvementsData,
  recurringErrorsData,
  opponentErrorsData,
  criticalPositionsData,
  modelGamesData,
  mostBrokenPrincipleQuote = 'Too many early pawn moves',
  principleWiseScores,
  onPracticePrinciples,
  colorBreakdown,
  scoreBandLegend,
  forPdfExport = false,
}) => {
  const { isMobileOrTab } = useMediaQuery();
  const [colorTab, setColorTab] = useState<'all' | 'white' | 'black'>('all');
  const [bandLegendOpen, setBandLegendOpen] = useState(false);

  const sl = colorBreakdown?.[forPdfExport ? 'all' : colorTab];
  const showColorTabs = Boolean(colorBreakdown) && !forPdfExport;
  const showPdfColorOverview = Boolean(colorBreakdown) && forPdfExport;
  const pdfLikeLayout = forPdfExport;

  useEffect(() => {
    if (forPdfExport) {
      setBandLegendOpen(true);
    }
  }, [forPdfExport]);

  const showBandDefinitions = bandLegendOpen || forPdfExport;

  const oScore = sl?.opening_score ?? openingScore;
  const oAccuracy = sl?.opening_accuracy ?? openingAccuracy;
  const oBest = sl?.best_game_score ?? bestGameScore;
  const oLowest = sl?.lowest_game_score ?? lowestGameScore;
  const oGamesN = sl?.games_analyzed ?? gamesAnalyzed;
  const oGameCards = sl?.game_by_game_performance ?? gameScoreCards;
  const oPrinciples = sl?.principle_wise_scores ?? principleWiseScores;
  const oScoreBand = sl?.score_band ?? scoreBand;
  const oBrokenQuote = sl?.most_broken_principle?.quote ?? mostBrokenPrincipleQuote;

  // Improvements data with correct colors from Figma
  const improvements = sl?.improvements ?? improvementsData ?? [
    { title: 'Development', score: 75, color: '#E17846' },
    { title: 'Center Control', score: 75, color: '#937ADB' },
    { title: 'Pawn Coordination', score: 75, color: '#DB7ACC' },
    { title: 'Piece Equality', score: 75, color: '#67B3E1' },
    { title: 'King Safety', score: 75, color: '#3DAB9E' },
  ];

  const principlePalette = ['#E17846', '#937ADB', '#DB7ACC', '#67B3E1', '#3DAB9E', '#F2C14E', '#2D9CDB'];

  const overallBars = (() => {
    const bars: { title: string; score: number; color: string }[] = [];

    if (oAccuracy != null) {
      const accuracyScore = Number(oAccuracy);
      if (!Number.isNaN(accuracyScore)) {
        bars.push({
          title: 'Opening Accuracy',
          score: Math.max(0, Math.min(100, accuracyScore)),
          color: CCColors.Primary.Default,
        });
      }
    }

    // Prefer full principle list for bars (not just top-5 improvements).
    const pRows = Array.isArray(oPrinciples) ? oPrinciples : [];
    if (pRows.length > 0) {
      pRows.forEach((row, idx) => {
        const score = Number((row as any)?.score);
        if (Number.isNaN(score)) return;
        const title = String((row as any)?.principle || '').replace(/_/g, ' ').trim();
        if (!title) return;
        bars.push({
          title: title
            .split(' ')
            .map(w => (w ? w[0].toUpperCase() + w.slice(1) : w))
            .join(' '),
          score: Math.max(0, Math.min(100, score)),
          color: principlePalette[idx % principlePalette.length],
        });
      });
      return bars;
    }

    // Fallback (legacy): show the 5 improvements if principle list missing.
    return [...bars, ...improvements];
  })();

  // Recurring errors data
  const recurringErrors =
    colorBreakdown != null
      ? (sl?.recurring_errors ?? []).map(r => ({
          title: r.title || '',
          count: r.count_label || `${r.count ?? 0}`,
        }))
      : (recurringErrorsData ?? [
          { title: 'Same piece moved twice early', count: '2 games' },
          { title: 'Early pawn pushes without reason', count: '2 games' },
          { title: 'Delayed castling', count: '2 games' },
        ]);

  // Opponent errors data
  const opponentErrors =
    colorBreakdown != null
      ? ((sl?.recurring_opponent_errors ?? sl?.opponent_errors) ?? []).map(r => ({
          title: r.title || '',
          count: r.count_label || `${r.count ?? 0}`,
        }))
      : (opponentErrorsData ?? [
          { title: 'Same piece moved twice early', count: '2 games' },
          { title: 'Early pawn pushes without reason', count: '2 games' },
        ]);

  // Critical positions data
  const criticalPositions = criticalPositionsData ?? [
    { title: 'Early knight sortie', count: '3 occurrences' },
    { title: 'Passive bishop placement', count: '3 occurrences' },
    { title: 'Missed central break timing', count: '3 occurrences' },
  ];

  // Model games data
  const modelGames = modelGamesData ?? [
    { title: 'Classic model game', reason: 'Better development structure' },
    { title: 'Positional punishment example', reason: 'Convert opponent inaccuracies' },
    { title: 'Modern GM example (similar structure)', reason: 'Better development structure' },
  ];

  const defaultPerformanceCards = [
    {
      title: 'Why your score is 75/100',
      content:
        'Your opening score of 75/100 reflects a solid foundation with room for improvement. You excel at piece development and maintaining central control, which is evident in most of your games. However, inconsistencies in pawn structure discipline and premature piece movements are holding you back from reaching the 80+ range. With more precise opening play, you can enter the middlegame with stronger positions.',
      tone: 'green',
    },
    {
      title: 'How you compare to very strong players (≈2500+)',
      content:
        "Grandmasters rated 2500+ typically score in the high 80s–90s on this opening blend. At 75/100 you are closer to steady club-level opening play than to elite consistency—closures usually come from fewer punished mistakes and steadier plans through move 15–20.",
      tone: 'pink',
    },
  ];

  // Performance summary cards (real reports override via performanceCardsData + tone)
  const performanceCards =
    colorBreakdown != null
      ? (sl?.performance_cards ?? []).map(c => ({
          title: c.title,
          content: c.content,
          tone: c.tone,
        }))
      : performanceCardsData ?? defaultPerformanceCards;

  const staticBandLegend: { key: string; label: string; range: string; description: string }[] = [
    { key: 'needs_focus', label: 'Needs focus', range: '0–54', description: 'Several recurring opening gaps still show up.' },
    { key: 'developing', label: 'Developing', range: '55–64', description: 'Workable habits with some inconsistency.' },
    { key: 'solid', label: 'Solid', range: '65–74', description: 'Playable structures; tighten details next.' },
    { key: 'strong', label: 'Strong', range: '75–84', description: 'Reliable competence; refine precision.' },
    { key: 'excellent', label: 'Excellent', range: '85–100', description: 'Close to very strong players on this metric.' },
  ];
  const bandLegendRows = scoreBandLegend?.length ? scoreBandLegend : staticBandLegend;

  const colorTabLabel = (key: 'all' | 'white' | 'black') => {
    if (!colorBreakdown) return key === 'all' ? 'All' : key === 'white' ? 'White' : 'Black';
    const slice = colorBreakdown[key];
    const pct =
      slice?.has_games && slice.opening_score != null
        ? ` (${Number(slice.opening_score).toFixed(1)}%)`
        : '';
    if (key === 'all') return `All${pct}`;
    if (key === 'white') return `White${pct}`;
    return `Black${pct}`;
  };

  const renderPerformanceSection = () => (
    <View style={styles.card}>
      <CCText style={styles.cardTitle}>Performance Summary</CCText>
      {sectionNotes?.performance_summary ? (
        <CCText style={styles.sectionHint}>{sectionNotes.performance_summary}</CCText>
      ) : null}
      {performanceCards.map((card, index) => {
        const surface = performanceCardSurface(card);
        return (
          <View
            key={index}
            style={[
              styles.performanceCard,
              { backgroundColor: surface.backgroundColor, borderColor: surface.borderColor },
            ]}>
            <CCText style={styles.performanceCardTitle}>{card.title}</CCText>
            <CCText style={styles.performanceCardContent}>{card.content}</CCText>
          </View>
        );
      })}
    </View>
  );

  const renderGamesSection = () => {
    const tiles = (oGameCards && oGameCards.length > 0
      ? oGameCards.map((item, index) => ({
          score: item?.score ?? 0,
          label: item?.game_label || `Game ${index + 1}`,
          game_uuid: item?.game_uuid,
        }))
      : gameScores.map((score, index) => ({
          score,
          label: `Game ${index + 1}`,
          game_uuid: undefined,
        }))
    ).map((item, index) => (
      <Pressable
        key={index}
        disabled={forPdfExport || !item.game_uuid || item?.score === 0}
        onPress={() => item.game_uuid && item?.score !== 0 && onOpenGameReviewFromScore?.(item.game_uuid)}
        style={[styles.gameScoreCard, isMobileOrTab && styles.gameScoreCardMobile]}>
        {item?.score === 0 ? (
          <Tooltip
            text={'Not enough moves to analyse'}
            backgroundColor={CCColors.Black}
            textcolor={CCColors.White}
            borderColor={CCColors.Black}
            arrowColor={CCColors.Black}
            fontSize={12}
            width={250}
            paddingVertical={8}
            borderWidth={1}
          >
            {toggleEvent => (
              <View onPointerEnter={toggleEvent} onPointerLeave={toggleEvent}>
                <CCText style={styles.gameScoreValue}>{"--"}</CCText>
              </View>
            )}
          </Tooltip>
        ) : (
          <CCText style={styles.gameScoreValue}>
            {item?.score}
          </CCText>
        )}
        <CCText style={styles.gameScoreLabel}>{item.label}</CCText>
      </Pressable>
    ));

    return (
      <View style={styles.card}>
        <CCText style={styles.cardTitle}>Game-by-Game Performance</CCText>
        {sectionNotes?.game_by_game ? (
          <CCText style={styles.sectionHint}>{sectionNotes.game_by_game}</CCText>
        ) : null}
        {pdfLikeLayout || !isMobileOrTab ? (
          <View style={styles.gameScoresRow}>{tiles}</View>
        ) : (
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={styles.gameScoresRowScroll}>
            <View style={[styles.gameScoresRow, styles.gameScoresRowMobile]}>{tiles}</View>
          </ScrollView>
        )}
      </View>
    );
  };

  const renderPrinciplesSection = () =>
    oPrinciples && oPrinciples.length > 0 ? (
      <View style={styles.card}>
        <CCText style={styles.cardTitle}>Opening principles breakdown</CCText>
        {sectionNotes?.principle_wise ? (
          <CCText style={styles.sectionHint}>{sectionNotes.principle_wise}</CCText>
        ) : null}
        {oPrinciples.map((row, index) => {
          const name = (row.principle || 'Principle').replace(/_/g, ' ');
          const scoreRounded = row.score != null ? Math.round(Number(row.score)) : null;
          const status = row.status_label;
          return (
            <Pressable
              key={`${row.principle ?? index}`}
              disabled={true}
              style={styles.principleWiseRow}>
              <View style={styles.principleWiseHeader}>
                <CCText style={styles.principleWiseName}>{name}</CCText>
                {status ? (
                  <View
                    style={[
                      styles.principleStatusPill,
                      { borderColor: principleStatusColor(status) },
                    ]}>
                    <CCText style={[styles.principleStatusText, { color: principleStatusColor(status) }]}>
                      {status}
                    </CCText>
                  </View>
                ) : null}
              </View>
              <CCText style={styles.principleScoreLine}>
                Theme score: {scoreRounded != null ? `${scoreRounded} / 100` : '—'}
                {row.followed != null || row.violated != null
                  ? ` · principle followed ${row.followed ?? 0}× · punished slips ${row.violated ?? 0}×`
                  : ''}
              </CCText>
              {row.summary_line ? (
                <CCText style={styles.principleNarrative}>{row.summary_line}</CCText>
              ) : null}
              {row.confidence_note ? (
                <CCText style={styles.principleConfidence}>{row.confidence_note}</CCText>
              ) : null}
            </Pressable>
          );
        })}
      </View>
    ) : null;

  const renderBarsSection = () => (
    <View style={[styles.twoColumnContainer, isMobileOrTab && styles.twoColumnContainerMobile]}>
      <View style={[styles.columnCard, isMobileOrTab && styles.columnCardFull]}>
        <CCText style={styles.smallCardTitle}>Principle scores (bars)</CCText>
        {sectionNotes?.opening_principles_bars ? (
          <CCText style={styles.sectionHintMuted}>{sectionNotes.opening_principles_bars}</CCText>
        ) : null}
        <CCText style={styles.overallScoreText}>{oScore} / 100</CCText>
        {oAccuracy != null ? (
          <CCText style={styles.overallAccuracyText}>
            Opening accuracy (move stability): {oAccuracy} / 100
          </CCText>
        ) : null}
        {overallBars.map((item, index) => (
          <Box
            key={index}
            flexDirection={isMobileOrTab ? 'column' : 'row'}
            gap={20}
            alignItems={isMobileOrTab ? 'stretch' : 'center'}
            style={isMobileOrTab ? styles.progressRowMobile : undefined}>
            <View style={styles.progressItem}>
              <View style={styles.progressHeader}>
                <CCText style={styles.progressTitle}>{item.title}</CCText>
                <CCText style={[styles.progressScore, { color: item.color }]}>{item.score}</CCText>
              </View>
              <View style={styles.progressBarBg}>
                <View
                  style={[
                    styles.progressBarFill,
                    { width: `${item.score}%`, backgroundColor: item.color },
                  ]}
                />
              </View>
            </View>
            {item.title !== 'Opening Accuracy' && !forPdfExport ? (
              <CCButton
                type="fill"
                titleStyle={{ fontSize: 12, color: CCColors.Black }}
                onPress={() => onPracticePrinciples?.(item.title)}>
                Practice Principles
              </CCButton>
            ) : null}
          </Box>
        ))}
        <Box
          flexDirection={isMobileOrTab ? 'column' : 'row'}
          gap={20}
          style={[styles.mostBrokenCard, { borderColor: CCColors.Red }]}>
          <Ionicons name="alert-circle-outline" size={30} color={CCColors.Red} />
          <View>
            <View style={styles.mostBrokenHeader}>
              <CCText style={styles.mostBrokenTitle}>Most Broken Principle</CCText>
            </View>
            <CCText style={styles.mostBrokenQuote}>"{oBrokenQuote}"</CCText>
          </View>
        </Box>
      </View>
    </View>
  );

  return (
    <View style={styles.container}>
      <ScrollView
        style={styles.scrollView}
        showsVerticalScrollIndicator={false}
        testID={forPdfExport ? 'oireport-ready' : 'opening-report-download-root'}>
        {/* Header */}
        {!forPdfExport ? (
          <View style={[styles.header, isMobileOrTab && styles.headerMobile]}>
            <View style={styles.headerLeft}>
              <CCButton
                hoverEffect={false}
                onPress={onBackPress}
                type="none"
                style={styles.backButton}
                contentContainerStyle={{ paddingHorizontal: 0, paddingVertical: 0 }}>
                <Box flexDirection='row' alignItems='center' gap={10}>
                  <Ionicons name="arrow-back" size={20} color={CCColors.TextColor.Dark} />
                  <CCText style={styles.backText}>Back</CCText>
                </Box>
              </CCButton>
            </View>
            {headerRightAction ? (
              <View style={styles.headerRight}>{headerRightAction}</View>
            ) : null}
          </View>
        ) : null}

        {/* Title Section */}
        <View style={styles.titleSection}>
          <View style={styles.titleRow}>
            <CCText style={styles.mainTitle}>Opening Performance Report</CCText>
          </View>
          <CCText style={styles.subtitle}>
            Last {oGamesN} games analyzed • Report date: {reportDate}
          </CCText>
          {reportIntro ? (
            <CCText style={styles.sectionHint}>{reportIntro}</CCText>
          ) : null}
          {showColorTabs ? (
            <View style={styles.colorTabWrap}>
              {(['all', 'white', 'black'] as const).map(key => {
                const active = colorTab === key;
                return (
                  <Pressable
                    key={key}
                    disabled={forPdfExport}
                    onPress={() => setColorTab(key)}
                    style={[styles.colorTabSeg, active && styles.colorTabSegActive]}>
                    <CCText
                      numberOfLines={1}
                      style={[styles.colorTabSegText, active && styles.colorTabSegTextActive]}>
                      {colorTabLabel(key)}
                    </CCText>
                  </Pressable>
                );
              })}
            </View>
          ) : null}
          {showColorTabs && sl && sl.has_games === false && colorTab !== 'all' ? (
            <CCText style={styles.sectionHint}>
              No games in this report for {colorTab === 'white' ? 'White' : 'Black'}.
            </CCText>
          ) : null}
        </View>

        {showPdfColorOverview && colorBreakdown ? (
          <View style={styles.pdfOverviewCard}>
            <CCText style={styles.pdfOverviewTitle}>Scores by side (All games · White · Black)</CCText>
            <CCText style={styles.sectionHintMuted}>
              Overview for each slice. Sections below summarize all games together unless noted.
            </CCText>
            {(['all', 'white', 'black'] as const).map((key, idx) => {
              const slice = colorBreakdown[key];
              if (!slice) {
                return null;
              }
              const accPct =
                slice.opening_accuracy != null && slice.has_games !== false
                  ? `${Number(slice.opening_accuracy).toFixed(1)}%`
                  : '—';
              const gamesLabel =
                slice.games_analyzed != null ? String(slice.games_analyzed) : '—';
              return (
                <View
                  key={key}
                  style={[styles.pdfOverviewRow, idx > 0 ? styles.pdfOverviewRowDivider : null]}>
                  <CCText style={styles.pdfOverviewRowTitle}>{colorTabLabel(key)}</CCText>
                  <CCText style={styles.pdfOverviewRowBody}>
                    Opening score {slice.opening_score ?? '—'}/100 · Move-stability accuracy {accPct} ·
                    Games {gamesLabel} · Best {slice.best_game_score ?? '—'} · Lowest{' '}
                    {slice.lowest_game_score ?? '—'}
                  </CCText>
                  {slice.has_games === false && key !== 'all' ? (
                    <CCText style={styles.sectionHintMuted}>No games in this sample for this color.</CCText>
                  ) : null}
                </View>
              );
            })}
          </View>
        ) : null}

        {/* Overall Opening Score Card */}
        <View style={styles.card}>
          <View style={styles.cardHeaderRow}>
            <CCText style={styles.cardTitle}>Overall Opening Score</CCText>
          </View>
          {sectionNotes?.overall_opening_score ? (
            <CCText style={styles.sectionHint}>{sectionNotes.overall_opening_score}</CCText>
          ) : null}

          <View style={[styles.scoreStatsRow, isMobileOrTab && styles.scoreStatsRowMobile]}>
            <View style={[styles.mainScoreCard, isMobileOrTab && styles.mainScoreCardMobile]}>
              <LinearGradient
                colors={['rgba(250, 207, 71, 0.4)', 'rgba(250, 207, 71, 0.6)']}
                style={styles.gradientScoreBg}
              />
              <View style={styles.scoreContent}>
                <CCText style={styles.mainScore}>{oScore}</CCText>
                <CCText style={styles.scoreLabel}>Out of 100</CCText>
                {oScoreBand?.label ? (
                  <View
                    style={[
                      styles.bandPill,
                      { borderColor: overallScoreBandColor(oScoreBand.label) },
                    ]}>
                    <CCText
                      style={[styles.bandPillText, { color: overallScoreBandColor(oScoreBand.label) }]}>
                      {oScoreBand.label}
                    </CCText>
                  </View>
                ) : null}
              </View>
            </View>

            <View style={[styles.statCard, isMobileOrTab && styles.statCardMobile]}>
              <CCText style={styles.statScore}>{oBest}</CCText>
              <CCText style={styles.statLabel}>Best game score</CCText>
            </View>

            <View style={[styles.statCard, isMobileOrTab && styles.statCardMobile]}>
              <CCText style={styles.statScore}>{oLowest}</CCText>
              <CCText style={styles.statLabel}>Lowest game score</CCText>
            </View>
          </View>

          {bandLegendRows.length > 0 ? (
            <View style={styles.bandLegendBlock}>
              {forPdfExport ? (
                <CCText style={styles.bandLegendStaticHeading}>What these bands mean</CCText>
              ) : (
                <Pressable
                  onPress={() => setBandLegendOpen(!bandLegendOpen)}
                  style={styles.bandLegendToggle}>
                  <CCText style={styles.bandLegendToggleText}>What do these bands mean?</CCText>
                  <Feather
                    name={bandLegendOpen ? 'chevron-up' : 'chevron-down'}
                    size={16}
                    color={CCColors.TextColor.Primary}
                  />
                </Pressable>
              )}
              {showBandDefinitions ? (
                <View style={styles.bandLegendList}>
                  {bandLegendRows.map(row => (
                    <View key={row.key ?? row.label} style={styles.bandLegendRow}>
                      <CCText style={styles.bandLegendTitle}>
                        {row.label}
                        {row.range ? ` (${row.range})` : ''}
                      </CCText>
                      {row.description ? (
                        <CCText style={styles.bandLegendDesc}>{row.description}</CCText>
                      ) : null}
                    </View>
                  ))}
                </View>
              ) : null}
            </View>
          ) : null}
        </View>

        {isMobileOrTab ? (
          <>
            {renderBarsSection()}
            {renderPrinciplesSection()}
            {renderGamesSection()}
            {renderPerformanceSection()}
          </>
        ) : (
          <>
            {renderPerformanceSection()}
            {renderGamesSection()}
            {renderBarsSection()}
            {renderPrinciplesSection()}
          </>
        )}

        {/* Recurring Opening Errors */}
        <View style={[styles.card, { backgroundColor: 'transparent', shadowOffset: { width: 0, height: 0 }, }]}>
          <CCText style={styles.cardTitle}>Recurring Opening Errors</CCText>
          {sectionNotes?.recurring_errors ? (
            <CCText style={styles.sectionHint}>{sectionNotes.recurring_errors}</CCText>
          ) : null}
          <View style={[styles.threeColumnContainer, isMobileOrTab && styles.threeColumnContainerMobile]}>
            {/* Your Errors */}
            <View style={[styles.errorColumn, isMobileOrTab && styles.errorColumnMobile]}>
              <View>
                <CCText style={styles.columnTitle}>Your errors</CCText>
                {recurringErrors.map((error, index) => (
                  <View key={index} style={styles.errorItem}>
                    <CCText style={styles.errorTitle}>{error.title}</CCText>
                    <CCText style={styles.errorCount}>{error.count}</CCText>
                  </View>
                ))}
              </View>
              {!forPdfExport ? (
                <CCButton
                  style={styles.actionButtonFull}
                  onPress={onSolvePatternDrills}
                  type="fill"
                  contentContainerStyle={{ paddingHorizontal: 16, paddingVertical: 10 }}>
                  <CCText style={styles.actionButtonText}>Solve pattern drills</CCText>
                </CCButton>
              ) : null}
            </View>

            {/* Opponent Errors */}
            <View style={[styles.errorColumn, isMobileOrTab && styles.errorColumnMobile]}>
              <View>
                <CCText style={styles.columnTitle}>Opponent Errors</CCText>
                {opponentErrors.map((error, index) => (
                  <View key={index} style={styles.errorItem}>
                    <CCText style={styles.errorTitle}>{error.title}</CCText>
                    <CCText style={styles.errorCount}>{error.count}</CCText>
                  </View>
                ))}
              </View>
              {!forPdfExport ? (
                <CCButton
                  style={styles.actionButtonFull}
                  onPress={onTrainPunishments}
                  type="fill"
                  contentContainerStyle={{ paddingHorizontal: 16, paddingVertical: 10 }}>
                  <CCText style={styles.actionButtonText}>Train punishments</CCText>
                </CCButton>
              ) : null}
            </View>

            {/* Critical Positions */}
            {/* <View style={styles.errorColumn}>
              <CCText style={styles.columnTitle}>Critical Recurring Positions</CCText>
              {criticalPositions.map((position, index) => (
                <View key={index} style={styles.errorItem}>
                  <CCText style={styles.errorTitle}>{position.title}</CCText>
                  <CCText style={styles.errorCount}>{position.count}</CCText>
                </View>
              ))}
              <CCButton
                style={styles.actionButtonFull}
                onPress={onSolvePatternDrills}
                type="fill"
                contentContainerStyle={{ paddingHorizontal: 16, paddingVertical: 10 }}>
                <CCText style={styles.actionButtonText}>Solve pattern drills</CCText>
              </CCButton>
            </View> */}
          </View>
        </View>


        {/* Model Games to Study */}
        <View style={styles.card}>
          <CCText style={styles.cardTitle}>Model Games to Study</CCText>
          {sectionNotes?.model_games ? (
            <CCText style={styles.sectionHint}>{sectionNotes.model_games}</CCText>
          ) : null}
          <Spacer spacing={20} />
          {modelGames.map((game, index) => (
            <View key={index} style={styles.modelGameCard}>
              <View style={styles.modelGameContent}>
                <CCText style={styles.modelGameTitle}>{game.title}</CCText>
                <CCText style={styles.modelGameReason}>Reason: {game.reason}</CCText>
              </View>
              <View>
                <View style={styles.modelGameButtons}>
                  {!forPdfExport ? (
                    <CCButton
                      onPress={() => onOpenModelGame?.(index)}
                      type="outline"
                      style={styles.outlineButton}
                      contentContainerStyle={{ paddingHorizontal: 16, paddingVertical: 8 }}>
                      <CCText
                        style={[
                          styles.outlineButtonText,
                          { color: CCColors.TextColor.Dark },
                        ]}>
                        Open
                      </CCText>
                    </CCButton>
                  ) : null}
                </View>
              </View>
            </View>
          ))}
        </View>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: CCColors.Primary.Light,
  },
  scrollView: {
    flex: 1,
    paddingHorizontal: 16,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 16,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  backIcon: {
    width: 20,
    height: 20,
    marginRight: 8,
  },
  backText: {
    fontSize: FontSizes[16],
    color: CCColors.TextColor.Dark,
  },
  headerRight: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  titleSection: {
    marginBottom: 16,
  },
  titleRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  mainTitle: {
    fontSize: FontSizes[24],
    fontWeight: '700',
    color: CCColors.TextColor.Dark,
  },
  runAssessmentButton: {
    backgroundColor: CCColors.Primary.Default,
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 32,
  },
  runAssessmentText: {
    fontSize: FontSizes[20],
    fontWeight: '500',
    color: CCColors.TextColor.Dark,
  },
  subtitle: {
    fontSize: FontSizes[14],
    color: 'rgba(77, 63, 55, 0.8)',
    marginTop: 4,
  },
  sectionHint: {
    fontSize: FontSizes[13],
    color: 'rgba(77, 63, 55, 0.78)',
    lineHeight: 18,
    marginTop: 8,
    marginBottom: 12,
  },
  sectionHintMuted: {
    fontSize: FontSizes[12],
    color: 'rgba(77, 63, 55, 0.68)',
    lineHeight: 17,
    marginBottom: 8,
  },
  card: {
    backgroundColor: CCColors.White,
    borderRadius: 16,
    padding: 16,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  cardHeaderRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  cardTitle: {
    fontSize: FontSizes[20],
    fontWeight: '500',
    color: CCColors.TextColor.Primary,
  },
  smallCardTitle: {
    fontSize: FontSizes[16],
    fontWeight: '500',
    color: 'rgba(77, 63, 55, 0.8)',
    marginBottom: 4,
  },
  seeLeaderboardButton: {
    backgroundColor: CCColors.Primary.Default,
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 32,
  },
  seeLeaderboardText: {
    fontSize: FontSizes[14],
    fontWeight: '500',
    color: CCColors.TextColor.Dark,
  },
  scoreStatsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 8,
    height: 150
  },
  mainScoreCard: {
    width: '42%',
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
  },
  gradientScoreBg: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
  },
  scoreContent: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  mainScore: {
    fontSize: 40,
    fontWeight: '700',
    color: CCColors.TextColor.Primary,
  },
  scoreLabel: {
    fontSize: FontSizes[14],
    color: CCColors.TextColor.Primary,
  },
  bandPill: {
    marginTop: 8,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 20,
    borderWidth: 1,
    backgroundColor: 'rgba(255,255,255,0.6)',
  },
  bandPillText: {
    fontSize: FontSizes[12],
    fontWeight: '600',
  },
  statCard: {
    width: '26%',
    aspectRatio: 1,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: CCColors.White,
    borderWidth: 1,
    borderColor: 'rgba(38, 35, 34, 0.2)',
  },
  statScore: {
    fontSize: 40,
    fontWeight: '700',
    color: CCColors.TextColor.Primary,
  },
  statLabel: {
    fontSize: FontSizes[14],
    color: CCColors.TextColor.Primary,
    textAlign: 'center',
  },
  gameScoresRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  gameScoreCard: {
    width: '14%',
    aspectRatio: 0.8,
    borderRadius: 8,
    backgroundColor: CCColors.Primary.Light,
    borderWidth: 1,
    borderColor: 'rgba(38, 35, 34, 0.2)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  gameScoreValue: {
    fontSize: 30,
    fontWeight: '700',
    color: CCColors.TextColor.Primary,
  },
  gameScoreLabel: {
    fontSize: FontSizes[14],
    color: CCColors.TextColor.Primary,
  },
  performanceCard: {
    padding: 16,
    borderRadius: 8,
    borderWidth: 1,
    marginBottom: 12,
  },
  performanceCardTitle: {
    fontSize: FontSizes[16],
    fontWeight: '600',
    color: CCColors.TextColor.Primary,
    marginBottom: 8,
  },
  performanceCardContent: {
    fontSize: FontSizes[14],
    color: 'rgba(77, 63, 55, 0.8)',
    lineHeight: 20,
  },
  twoColumnContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  columnCard: {
    flexGrow: 1,
    backgroundColor: CCColors.White,
    borderRadius: 16,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  bestMomentCard: {
    padding: 16,
    borderRadius: 8,
    borderWidth: 1,
    marginBottom: 16,
    backgroundColor: 'rgba(61, 171, 158, 0.08)',
  },
  bestMomentTitle: {
    fontSize: FontSizes[16],
    fontWeight: '600',
    color: CCColors.TextColor.Primary,
    marginBottom: 4,
  },
  bestMomentQuote: {
    fontSize: FontSizes[14],
    fontStyle: 'italic',
    color: 'rgba(77, 63, 55, 0.8)',
  },
  strengthItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  checkIconBg: {
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  smallCheckIcon: {
    width: 16,
    height: 16,
    tintColor: CCColors.White,
  },
  strengthContent: {
    flex: 1,
  },
  strengthTitle: {
    fontSize: FontSizes[13],
    fontWeight: '600',
    color: CCColors.TextColor.Primary,
  },
  strengthGames: {
    fontSize: FontSizes[14],
    color: 'rgba(77, 63, 55, 0.8)',
  },
  buttonRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 16,
  },
  actionButton: {
    backgroundColor: CCColors.Primary.Default,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 32,
    flex: 1,
    marginHorizontal: 4,
    alignItems: 'center',
  },
  actionButtonFull: {
    backgroundColor: CCColors.Primary.Default,
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 32,
    alignItems: 'center',
    marginTop: 12,
  },
  actionButtonText: {
    fontSize: FontSizes[14],
    fontWeight: '500',
    color: CCColors.TextColor.Dark,
  },
  overallScoreText: {
    fontSize: 24,
    fontWeight: '700',
    color: CCColors.TextColor.Primary,
    marginBottom: 16,
  },
  overallAccuracyText: {
    fontSize: FontSizes[14],
    fontWeight: '500',
    color: CCColors.TextColor.Primary,
    marginTop: -10,
    marginBottom: 16,
    opacity: 0.8,
  },
  mostBrokenCard: {
    padding: 16,
    borderRadius: 8,
    borderWidth: 1,
    marginBottom: 16,
    backgroundColor: 'rgba(225, 120, 70, 0.08)',
  },
  mostBrokenHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  alertIcon: {
    width: 24,
    height: 24,
    marginRight: 8,
  },
  mostBrokenTitle: {
    fontSize: FontSizes[16],
    fontWeight: '600',
    color: CCColors.TextColor.Primary,
    marginBottom: 4,
  },
  mostBrokenQuote: {
    fontSize: FontSizes[14],
    color: CCColors.Red,
  },
  progressItem: {
    marginBottom: 12,
    flex: 1,
  },
  progressHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  progressTitle: {
    fontSize: FontSizes[14],
    fontWeight: '500',
    color: CCColors.TextColor.Primary,
  },
  progressScore: {
    fontSize: FontSizes[14],
    fontWeight: '700',
  },
  progressBarBg: {
    height: 16,
    backgroundColor: '#F6F6F6',
    borderRadius: 30,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: '#D9D9D9',
  },
  progressBarFill: {
    height: '100%',
    borderRadius: 30,
  },
  threeColumnContainer: {
    marginTop: 20,
    flexDirection: 'row',
    justifyContent: 'flex-start',
    gap: 12,
  },
  errorColumn: {
    backgroundColor: CCColors.White,
    borderRadius: 16,
    padding: 24,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
    justifyContent: 'space-between',
  },
  columnTitle: {
    fontSize: FontSizes[16],
    fontWeight: '700',
    color: CCColors.TextColor.Primary,
    marginBottom: 12,
  },
  errorItem: {
    marginBottom: 12,
  },
  errorTitle: {
    fontSize: FontSizes[13],
    fontWeight: '600',
    color: CCColors.TextColor.Primary,
    marginBottom: 2,
  },
  errorCount: {
    fontSize: FontSizes[14],
    color: 'rgba(77, 63, 55, 0.8)',
  },
  modelGameCard: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
    backgroundColor: CCColors.White,
    borderWidth: 1,
    borderColor: 'rgba(38, 35, 34, 0.2)',
    borderRadius: 8,
    padding: 16,
    marginBottom: 12,
  },
  modelGameContent: {
    marginBottom: 12,
  },
  modelGameTitle: {
    fontSize: FontSizes[14],
    fontWeight: '600',
    color: CCColors.TextColor.Primary,
    marginBottom: 4,
  },
  modelGameReason: {
    fontSize: FontSizes[14],
    color: '#A8A7A6',
  },
  modelGameButtons: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 12,
  },
  outlineButton: {
    backgroundColor: CCColors.Primary.Light,
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 32,
  },
  outlineButtonText: {
    fontSize: FontSizes[14],
    fontWeight: '500',
    color: CCColors.TextColor.Dark,
  },
  principleWiseRow: {
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(38, 35, 34, 0.08)',
  },
  principleWiseHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    marginBottom: 4,
  },
  principleWiseName: {
    fontSize: FontSizes[14],
    fontWeight: '600',
    color: CCColors.TextColor.Primary,
    textTransform: 'capitalize',
    flex: 1,
  },
  principleStatusPill: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 12,
    borderWidth: 1,
    backgroundColor: 'rgba(255,255,255,0.85)',
  },
  principleStatusText: {
    fontSize: FontSizes[11],
    fontWeight: '700',
  },
  principleScoreLine: {
    fontSize: FontSizes[12],
    color: 'rgba(77, 63, 55, 0.82)',
    marginTop: 2,
  },
  principleNarrative: {
    fontSize: FontSizes[12],
    color: 'rgba(77, 63, 55, 0.75)',
    lineHeight: 17,
    marginTop: 6,
  },
  principleConfidence: {
    fontSize: FontSizes[11],
    color: 'rgba(77, 63, 55, 0.6)',
    fontStyle: 'italic',
    marginTop: 4,
  },
  principleTapHint: {
    fontSize: FontSizes[11],
    color: CCColors.Primary.Default,
    marginTop: 8,
    fontWeight: '500',
  },
  principleWiseMeta: {
    fontSize: FontSizes[12],
    color: 'rgba(77, 63, 55, 0.75)',
    marginTop: 4,
  },
  headerMobile: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 12,
  },
  colorTabWrap: {
    flexDirection: 'row',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: 'rgba(38, 35, 34, 0.2)',
    overflow: 'hidden',
    marginTop: 12,
    alignSelf: 'stretch',
  },
  colorTabSeg: {
    flex: 1,
    paddingVertical: 10,
    paddingHorizontal: 6,
    backgroundColor: CCColors.White,
    alignItems: 'center',
    justifyContent: 'center',
  },
  colorTabSegActive: {
    backgroundColor: CCColors.BrownShade2,
  },
  colorTabSegText: {
    fontSize: FontSizes[12],
    fontWeight: '600',
    color: CCColors.TextColor.Primary,
    textAlign: 'center',
  },
  colorTabSegTextActive: {
    color: CCColors.White,
  },
  scoreStatsRowMobile: {
    flexDirection: 'column',
    height: undefined,
    gap: 12,
  },
  mainScoreCardMobile: {
    width: '100%',
    minHeight: 140,
    paddingVertical: 16,
  },
  statCardMobile: {
    width: '100%',
    aspectRatio: undefined,
    minHeight: 88,
    paddingVertical: 12,
  },
  bandLegendBlock: {
    marginTop: 16,
    borderTopWidth: 1,
    borderTopColor: 'rgba(38, 35, 34, 0.08)',
    paddingTop: 12,
  },
  bandLegendStaticHeading: {
    fontSize: FontSizes[13],
    fontWeight: '600',
    color: CCColors.TextColor.Dark,
    marginBottom: 8,
  },
  pdfOverviewCard: {
    backgroundColor: CCColors.White,
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: 'rgba(38, 35, 34, 0.08)',
  },
  pdfOverviewTitle: {
    fontSize: FontSizes[16],
    fontWeight: '700',
    color: CCColors.TextColor.Dark,
    marginBottom: 6,
  },
  pdfOverviewRow: {
    marginTop: 4,
  },
  pdfOverviewRowDivider: {
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: 'rgba(38, 35, 34, 0.06)',
  },
  pdfOverviewRowTitle: {
    fontSize: FontSizes[14],
    fontWeight: '700',
    color: CCColors.TextColor.Primary,
    marginBottom: 4,
  },
  pdfOverviewRowBody: {
    fontSize: FontSizes[13],
    color: 'rgba(77, 63, 55, 0.88)',
    lineHeight: 20,
  },
  bandLegendToggle: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 4,
  },
  bandLegendToggleText: {
    fontSize: FontSizes[13],
    fontWeight: '600',
    color: CCColors.Primary.Default,
  },
  bandLegendList: {
    marginTop: 8,
    gap: 10,
  },
  bandLegendRow: {
    paddingVertical: 6,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(38, 35, 34, 0.06)',
  },
  bandLegendTitle: {
    fontSize: FontSizes[13],
    fontWeight: '600',
    color: CCColors.TextColor.Primary,
    marginBottom: 4,
  },
  bandLegendDesc: {
    fontSize: FontSizes[12],
    color: 'rgba(77, 63, 55, 0.78)',
    lineHeight: 17,
  },
  gameScoresRowScroll: {
    flexDirection: 'row',
    alignItems: 'stretch',
    paddingRight: 8,
  },
  gameScoresRowMobile: {
    flexDirection: 'row',
    gap: 10,
    justifyContent: 'flex-start',
    paddingVertical: 4,
  },
  gameScoreCardMobile: {
    width: 72,
    minWidth: 72,
    aspectRatio: 0.85,
  },
  twoColumnContainerMobile: {
    flexDirection: 'column',
  },
  columnCardFull: {
    width: '100%',
  },
  progressRowMobile: {
    marginBottom: 8,
  },
  threeColumnContainerMobile: {
    flexDirection: 'column',
  },
  errorColumnMobile: {
    width: '100%',
    marginBottom: 12,
  },
});

export default OpeningPerformanceReport;